// src/components/TemplatesToolbar.js
import { useState, useEffect } from "react";
import {
  Box,
  Stack,
  NativeSelect,
  FormControl,
  InputLabel,
  ImageList,
  ImageListItem,
  Typography,
  CircularProgress,
  OutlinedInput,
  Pagination,
} from "@mui/material";
import { IText, Image } from "fabric";
import { useData } from "../hooks/useData";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  startAfter,
  limit,
} from "firebase/firestore";
import { firestore } from "../firebase"; // Adjust path if needed

const TemplatesToolbar = ({ canvas }) => {
  const { setSelectedTool, setSelectedObject, setSnackbar } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [docId, setDocId] = useState(null);
  const [fbTemplates, setFbTemplates] = useState([]);
  const [templateCategories] = useState([
    { name: "Parking" },
    { name: "Trucking" },
  ]);
  const [searchTerm, setSearchTerm] = useState("Trucking");
  const [page, setPage] = useState(1);
  const [lastVisible, setLastVisible] = useState(null);
  const [count, setCount] = useState(1);

  // Handle canvas selection events
  useEffect(() => {
    if (canvas) {
      const handleSelection = (event) => {
        const activeObj = canvas.getActiveObject();
        setSelectedObject(activeObj);
        setSelectedTool(activeObj?.type || null);
      };
      const handleSelectionCleared = () => {
        setSelectedTool(null);
        setSelectedObject(null);
      };

      canvas.on("selection:created", handleSelection);
      canvas.on("selection:updated", handleSelection);
      canvas.on("selection:cleared", handleSelectionCleared);

      return () => {
        canvas.off("selection:created", handleSelection);
        canvas.off("selection:updated", handleSelection);
        canvas.off("selection:cleared", handleSelectionCleared);
      };
    }
  }, [canvas, setSelectedTool, setSelectedObject]);

  // Fetch templates when searchTerm or page changes
  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoading(true);
      try {
        const countQuery = query(
          collection(firestore, "templateThumbnails"),
          where("categoryName", "==", searchTerm)
        );
        const countSnapshot = await getDocs(countQuery);
        setCount(Math.ceil(countSnapshot.size / 6));

        const startAt = (page - 1) * 6;
        const templatesQuery = query(
          collection(firestore, "templateThumbnails"),
          where("categoryName", "==", searchTerm),
          orderBy("pos"),
          startAfter(startAt > 0 ? startAt - 1 : 0),
          limit(6)
        );
        const querySnapshot = await getDocs(templatesQuery);
        const templates = querySnapshot.docs.map((doc) => ({
          id: doc.data().id,
          name: doc.data().name,
          src: doc.data().src,
          categoryName: doc.data().categoryName,
        }));
        setFbTemplates(templates);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      } catch (error) {
        console.error("Error fetching templates:", error);
        setSnackbar({
          open: true,
          message: "Failed to load templates. Please try again.",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (searchTerm) {
      fetchTemplates();
    }
  }, [searchTerm, page, setSnackbar]);

  // Add template to canvas when docId and addingTemplate are set
  useEffect(() => {
    const addTemplateToCanvas = async () => {
      if (addingTemplate && docId && canvas) {
        try {
          const templateQuery = query(
            collection(firestore, "templateObjectData"),
            where("id", "==", Number(docId))
          );
          const querySnapshot = await getDocs(templateQuery);
          if (querySnapshot.empty) {
            throw new Error("Template not found");
          }

          const doc = querySnapshot.docs[0];
          const enlivenObject = JSON.parse(doc.data().enlivenObject);
          console.log("TemplatesToolbar: enlivenObject (full)", enlivenObject);

          if (!Array.isArray(enlivenObject)) {
            throw new Error(
              "Invalid template data: enlivenObject is not an array"
            );
          }

          // Manually create Fabric.js objects
          const createdObjects = [];
          for (const objData of enlivenObject) {
            let fabricType = objData.type;
            // Workaround for incorrect type (temporary)

            try {
              let obj;
              if (fabricType === "IText") {
                // Explicitly create IText with string text
                const text = String(objData.text || "Placeholder");
                console.log(
                  `TemplatesToolbar: IText text: ${text}, type: ${typeof text}`
                );
                obj = new IText(text, {
                  fontSize: objData.fontSize,
                  fontFamily: objData.fontFamily,
                  fontWeight: objData.fontWeight,
                  fontStyle: objData.fontStyle,
                  lineHeight: objData.lineHeight,
                  left: objData.left,
                  top: objData.top,
                  fill: objData.fill,
                  textAlign: objData.textAlign,
                  scaleX: objData.scaleX,
                  scaleY: objData.scaleY,
                  angle: objData.angle,
                  opacity: objData.opacity,
                  shadow: objData.shadow,
                  stroke: objData.stroke,
                  strokeWidth: objData.strokeWidth,
                  strokeUniform: objData.strokeUniform,
                  charSpacing: objData.charSpacing,
                  styles: objData.styles || [],
                  // Add other properties as needed
                });
              } else if (fabricType === "Image") {
                // Preload image to avoid drawImage errors
                if (!objData.src || typeof objData.src !== "string") {
                  console.warn(
                    `TemplatesToolbar: Invalid src for Image: ${objData.src}`
                  );
                  continue;
                }
                const img = await new Promise((resolve, reject) => {
                  const imgElement = new Image();
                  imgElement.src = objData.src;
                  imgElement.crossOrigin = objData.crossOrigin || "anonymous";
                  imgElement.onload = () => resolve(imgElement);
                  imgElement.onerror = () =>
                    reject(new Error(`Failed to load image: ${objData.src}`));
                });
                obj = new Image(img, {
                  left: objData.left,
                  top: objData.top,
                  width: objData.width,
                  height: objData.height,
                  scaleX: objData.scaleX,
                  scaleY: objData.scaleY,
                  angle: objData.angle,
                  opacity: objData.opacity,
                  fill: objData.fill,
                  stroke: objData.stroke,
                  strokeWidth: objData.strokeWidth,
                  strokeUniform: objData.strokeUniform,
                  filters: objData.filters || [],
                  cropX: objData.cropX,
                  cropY: objData.cropY,
                  crossOrigin: objData.crossOrigin,
                });
              } else {
                obj = new fabricType(objData);
              }
              createdObjects.push(obj);
              canvas.add(obj);
            } catch (err) {
              console.warn(
                `TemplatesToolbar: Failed to create object of type ${fabricType}`,
                err
              );
            }
          }

          if (createdObjects.length === 0) {
            throw new Error("No valid objects created from template");
          }

          canvas.requestRenderAll();
          setSelectedTool(null); // Return to Product Configuration
          setSnackbar({
            open: true,
            message: `Template added successfully (${createdObjects.length}/${enlivenObject.length} objects)`,
            severity:
              createdObjects.length < enlivenObject.length
                ? "warning"
                : "success",
          });
        } catch (error) {
          console.error("Error adding template:", error);
          setSnackbar({
            open: true,
            message: `Failed to add template: ${error.message}`,
            severity: "error",
          });
        } finally {
          setAddingTemplate(false);
          setDocId(null); // Reset docId to prevent re-runs
        }
      }
    };

    addTemplateToCanvas();
  }, [addingTemplate, docId, canvas, setSelectedTool, setSnackbar]);

  const handleSearchPageChange = (event, pageNumber) => {
    setPage(pageNumber);
  };

  const handleAddTemplate = (e) => {
    const id = e.target.dataset.id;
    if (id) {
      setDocId(id);
      setAddingTemplate(true);
    }
  };

  const handleCategorySelect = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          color: "#0d2445",
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Select Template
      </Typography>
      <Typography
        variant="body1"
        sx={{
          marginTop: "8px",
          color: "#5e5e5e",
          textAlign: "left",
        }}
      >
        Jump start your project with our free easy-to-use sign templates.
      </Typography>
      <Box sx={{ textAlign: "center" }}>
        <FormControl
          fullWidth
          variant="outlined"
          sx={{ marginTop: "20px", minWidth: "200px", marginBottom: "12px" }}
        >
          <InputLabel sx={{ color: "#5e5e5e" }} htmlFor="SignCategory">
            Select Category
          </InputLabel>
          <NativeSelect
            sx={{ "&.MuiInputBase-formControl": { bgcolor: "#fff" } }}
            defaultValue="Trucking"
            inputProps={{ id: "SignCategory" }}
            label="Select Category"
            input={<OutlinedInput label="Select Category" />}
            onChange={handleCategorySelect}
          >
            {templateCategories.map((category) => (
              <option key={category.name} value={category.name}>
                {category.name}
              </option>
            ))}
          </NativeSelect>
        </FormControl>
        <Pagination
          sx={{ backgroundColor: "#ffffff" }}
          page={page}
          onChange={handleSearchPageChange}
          count={count}
          size="small"
          color="primary"
          variant="outlined"
          shape="rounded"
          showFirstButton
          showLastButton
          siblingCount={1}
        />
      </Box>
      <Box sx={{ height: 600, minHeight: 600, overflowY: "scroll" }}>
        {isLoading && (
          <Stack direction="row" justifyContent="center">
            <CircularProgress />
          </Stack>
        )}
        {!isLoading && fbTemplates.length > 0 && (
          <ImageList cols={2} gap={0}>
            {fbTemplates.map((template) => (
              <ImageListItem
                key={template.id}
                sx={{
                  position: "relative",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderColor: "#eee",
                  "&:hover": { cursor: "pointer" },
                }}
              >
                <img
                  data-id={template.id}
                  src={template.src}
                  alt={template.name}
                  onClick={handleAddTemplate}
                  loading="lazy"
                  style={{ objectFit: "contain" }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </Box>
    </Box>
  );
};

export default TemplatesToolbar;
